import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

const SPLASH_DURATION = 4000;
const SPLASH_VIDEO_URL = '/videos/splash.mov';

const SplashScreen = ({ onEnd }) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const onEndRef = useRef(onEnd);
  const calledRef = useRef(false);
  onEndRef.current = onEnd;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!calledRef.current) {
        calledRef.current = true;
        onEndRef.current();
      }
    }, SPLASH_DURATION);
    return () => clearTimeout(timer);
  }, []); // runs once on mount — no dependencies to re-trigger it

  const handleSkip = () => {
    if (!calledRef.current) {
      calledRef.current = true;
      onEndRef.current();
    }
  };

  return (
    <Wrapper>
      <Text>🕸️Welcome to my Web🕸️</Text>

      {videoError ? (
        <FallbackContainer>
          <SpiderIcon>🕷️</SpiderIcon>
        </FallbackContainer>
      ) : (
        <>
          {!videoLoaded && (
            <FallbackContainer>
              <SpinningSpider>🕷️</SpinningSpider>
            </FallbackContainer>
          )}
          <Video
            data-testid="splash-video"
            src={SPLASH_VIDEO_URL}
            autoPlay
            muted
            playsInline
            preload="auto"
            onLoadedData={() => setVideoLoaded(true)}
            onError={() => setVideoError(true)}
            style={{ opacity: videoLoaded ? 1 : 0 }}
          />
        </>
      )}

      <SkipButton onClick={handleSkip}>Skip</SkipButton>
    </Wrapper>
  );
};

export default SplashScreen;

const popEffect = keyframes`
  0%   { transform: scale(0.5); opacity: 0; }
  50%  { transform: scale(1.25); opacity: 0.5; }
  100% { transform: scale(1); opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background-color: black;
  position: relative;
  overflow: hidden;
  text-align: center;
`;

const Text = styled.h1`
  color: #4c0065;
  animation: ${popEffect} 1.5s ease-in-out;
  text-shadow:
    0 0 5px white,
    0 0 10px white,
    0 0 15px white,
    0 0 20px white;
  font-family: 'Creepster';
  font-size: 5rem;
  position: fixed;
  top: 20%;
  left: 0;
  right: 0;
  margin: 0;
  padding: 0;
  z-index: 10;
  white-space: nowrap;
  pointer-events: none;

  @media (max-width: 600px) {
    font-size: 2.5rem;
    top: 15%;
  }
`;

const Video = styled.video`
  max-width: 80%;
  max-height: 70%;
  object-fit: contain;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  pointer-events: none;
  transition: opacity 0.3s ease;
`;

const FallbackContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SpiderIcon = styled.div`
  font-size: 4rem;
  animation: ${popEffect} 2s ease-in-out infinite;
`;

const SpinningSpider = styled.div`
  font-size: 3rem;
  animation: ${spin} 1s linear infinite;
`;

const SkipButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: rgba(76, 0, 101, 0.8);
  color: white;
  border: 2px solid #4c0065;
  border-radius: 8px;
  padding: 10px 15px;
  font-family: 'Creepster', Arial, sans-serif;
  font-size: 14px;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(76, 0, 101, 1);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;
